import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  Container, 
  Row, 
  Col, 
  ProgressBar, 
  Badge, 
  Spinner, 
  Alert, 
  Button 
} from "react-bootstrap";
import UserContext from "../../../contexts/UserContext";
import { getCategoryProgress, getCategoryVideos } from "../../../services/TrainingService";
import TrainingAccessControl from "./TrainingAccessControl";

const CategoryView = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [categoryData, setCategoryData] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (categoryId) {
      loadCategoryData();
    }
  }, [categoryId]);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      const [progressData, videosData] = await Promise.all([
        getCategoryProgress(categoryId),
        getCategoryVideos(categoryId),
      ]);
      
      setCategoryData(progressData);
      setVideos(videosData);
    } catch (error) {
      console.error("Erro ao carregar dados da categoria:", error);
      setError("Erro ao carregar dados da categoria");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = (video) => {
    if (video.is_unlocked) {
      navigate(`/training/video/${video.id}`);
    }
  };

  const getVideoStatusBadge = (video) => {
    if (video.is_completed) {
      return <Badge bg="success">Concluído</Badge>;
    } else if (!video.is_unlocked) {
      return <Badge bg="secondary">Bloqueado</Badge>;
    } else if (video.progress_percent > 0) {
      return <Badge bg="warning">Em Progresso</Badge>;
    } else {
      return <Badge bg="info">Disponível</Badge>;
    }
  };

  const getCategoryStatusBadge = () => {
    if (!categoryData) return null;
    
    if (categoryData.is_completed) {
      return <Badge bg="success">Categoria Concluída</Badge>;
    } else if (!categoryData.is_unlocked) {
      return <Badge bg="secondary">Categoria Bloqueada</Badge>;
    } else if (categoryData.completion_percentage > 0) {
      return <Badge bg="warning">Em Progresso</Badge>;
    } else {
      return <Badge bg="info">Disponível</Badge>;
    }
  };

  const formatVideoTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0 || !isFinite(seconds)) {
      return "0:00";
    }
    
    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando categoria...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="primary" onClick={() => navigate("/training")}>
          Voltar ao Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <TrainingAccessControl>
      <Container className="mt-4">
        {/* Header da Categoria */}
        <div className="page-header mb-4">
        <div className="d-flex align-items-center mb-2">
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="me-3"
            onClick={() => navigate("/training")}
          >
            <i className="las la-arrow-left"></i> Voltar
          </Button>
          <h1 className="mb-0">{categoryData?.category_name}</h1>
          <div className="ms-auto">
            {getCategoryStatusBadge()}
          </div>
        </div>
        
        {/* Progresso da Categoria */}
        <Card className="mb-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col xs={12} md={8}>
                <div className="d-flex justify-content-between mb-2">
                  <span>Progresso da Categoria</span>
                  <span className="fw-bold">
                    {categoryData?.completed_videos || 0} de {categoryData?.total_videos || 0} vídeos
                  </span>
                </div>
                <ProgressBar 
                  now={categoryData?.completion_percentage || 0} 
                  variant={categoryData?.is_completed ? "success" : "primary"}
                  style={{ height: "12px" }}
                />
              </Col>
              <Col xs={12} md={4} className="text-end">
                <h4 className="mb-0">{Math.round(categoryData?.completion_percentage || 0)}%</h4>
                <small className="text-muted">Concluído</small>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Lista de Vídeos */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">Vídeos da Categoria</h5>
        </Card.Header>
        <Card.Body>
          {Array.isArray(videos) && videos.length > 0 ? (
            <div className="videos-list">
              {videos.map((video, index) => (
                <div 
                  key={video.id} 
                  className={`video-item d-flex align-items-center p-3 border rounded mb-3 ${
                    video.is_unlocked ? 'video-unlocked' : 'video-locked'
                  }`}
                  style={{ 
                    cursor: video.is_unlocked ? 'pointer' : 'not-allowed',
                    opacity: video.is_unlocked ? 1 : 0.6 
                  }}
                  onClick={() => handleVideoClick(video)}
                >
                  {/* Número do Vídeo */}
                  <div className="video-number me-3">
                    <div 
                      className={`rounded-circle d-flex align-items-center justify-content-center ${
                        video.is_completed 
                          ? 'bg-success text-white' 
                          : video.is_unlocked 
                            ? 'bg-primary text-white' 
                            : 'bg-secondary text-white'
                      }`}
                      style={{ width: "40px", height: "40px", fontSize: "14px" }}
                    >
                      {video.is_completed ? (
                        <i className="las la-check"></i>
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                  </div>

                  {/* Thumbnail do Vídeo */}
                  <div className="video-thumbnail me-3">
                    {video.thumbnail_url ? (
                      <img 
                        src={video.thumbnail_url} 
                        alt={video.title}
                        className="rounded"
                        style={{ width: "120px", height: "80px", objectFit: "cover" }}
                      />
                    ) : (
                      <div 
                        className="bg-light d-flex align-items-center justify-content-center rounded"
                        style={{ width: "120px", height: "80px" }}
                      >
                        <i className="las la-play-circle" style={{ fontSize: "32px" }}></i>
                      </div>
                    )}
                    
                    {/* Overlay de lock se vídeo bloqueado */}
                    {!video.is_unlocked && (
                      <div 
                        className="position-absolute top-50 start-50 translate-middle bg-dark bg-opacity-75 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="las la-lock text-white"></i>
                      </div>
                    )}
                  </div>

                  {/* Informações do Vídeo */}
                  <div className="video-info flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0">{video.title}</h6>
                      {getVideoStatusBadge(video)}
                    </div>
                    
                    {/* Progresso do Vídeo */}
                    {video.is_unlocked && video.progress_percent > 0 && (
                      <div className="mb-2">
                        <div className="d-flex justify-content-between mb-1">
                          <small className="text-muted">Progresso</small>
                          <small className="text-muted">{Math.round(video.progress_percent)}%</small>
                        </div>
                        <ProgressBar 
                          now={video.progress_percent} 
                          variant={video.is_completed ? "success" : "primary"}
                          style={{ height: "6px" }}
                        />
                      </div>
                    )}

                    <div className="d-flex align-items-center text-muted small">
                      <span>Vídeo {index + 1}</span>
                      {video.last_position_seconds > 0 && (
                        <span className="ms-2">• Última posição: {formatVideoTime(video.last_position_seconds)}</span>
                      )}
                    </div>
                  </div>

                  {/* Ação */}
                  <div className="video-action">
                    {video.is_unlocked ? (
                      <i className="las la-chevron-right text-muted"></i>
                    ) : (
                      <i className="las la-lock text-muted"></i>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-muted py-4">
              <i className="las la-video" style={{ fontSize: "48px" }}></i>
              <p>Nenhum vídeo disponível nesta categoria</p>
            </div>
          )}
        </Card.Body>
      </Card>
      </Container>
    </TrainingAccessControl>
  );
};

export default CategoryView;